/**
 * This code was AUTOGENERATED using the Codama library.
 * Please DO NOT EDIT THIS FILE, instead use visitors
 * to add features, then rerun Codama to update it.
 *
 * @see https://github.com/codama-idl/codama
 */

import {
  assertAccountExists,
  assertAccountsExist,
  combineCodec,
  decodeAccount,
  fetchEncodedAccount,
  fetchEncodedAccounts,
  getAddressDecoder,
  getAddressEncoder,
  getArrayDecoder,
  getArrayEncoder,
  getOptionDecoder,
  getOptionEncoder,
  getStructDecoder,
  getStructEncoder,
  getU8Decoder,
  getU8Encoder,
  type Account,
  type Address,
  type Codec,
  type Decoder,
  type EncodedAccount,
  type Encoder,
  type FetchAccountConfig,
  type FetchAccountsConfig,
  type MaybeAccount,
  type MaybeEncodedAccount,
  type Option,
  type OptionOrNullable,
} from '@solana/kit';
import { getKeyDecoder, getKeyEncoder, type Key, type KeyArgs } from '../types';

export type ProgramAuthority = {
  key: Key;
  authority: Address;
  authorizedCreators: Array<Option<Address>>;
  creatorCount: number;
  pendingAuthority: Option<Address>;
  bump: number;
};

export type ProgramAuthorityArgs = {
  key: KeyArgs;
  authority: Address;
  authorizedCreators: Array<OptionOrNullable<Address>>;
  creatorCount: number;
  pendingAuthority: OptionOrNullable<Address>;
  bump: number;
};

export function getProgramAuthorityEncoder(): Encoder<ProgramAuthorityArgs> {
  return getStructEncoder([
    ['key', getKeyEncoder()],
    ['authority', getAddressEncoder()],
    [
      'authorizedCreators',
      getArrayEncoder(getOptionEncoder(getAddressEncoder()), { size: 10 }),
    ],
    ['creatorCount', getU8Encoder()],
    ['pendingAuthority', getOptionEncoder(getAddressEncoder())],
    ['bump', getU8Encoder()],
  ]);
}

export function getProgramAuthorityDecoder(): Decoder<ProgramAuthority> {
  return getStructDecoder([
    ['key', getKeyDecoder()],
    ['authority', getAddressDecoder()],
    [
      'authorizedCreators',
      getArrayDecoder(getOptionDecoder(getAddressDecoder()), { size: 10 }),
    ],
    ['creatorCount', getU8Decoder()],
    ['pendingAuthority', getOptionDecoder(getAddressDecoder())],
    ['bump', getU8Decoder()],
  ]);
}

export function getProgramAuthorityCodec(): Codec<
  ProgramAuthorityArgs,
  ProgramAuthority
> {
  return combineCodec(
    getProgramAuthorityEncoder(),
    getProgramAuthorityDecoder()
  );
}

export function decodeProgramAuthority<TAddress extends string = string>(
  encodedAccount: EncodedAccount<TAddress>
): Account<ProgramAuthority, TAddress>;
export function decodeProgramAuthority<TAddress extends string = string>(
  encodedAccount: MaybeEncodedAccount<TAddress>
): MaybeAccount<ProgramAuthority, TAddress>;
export function decodeProgramAuthority<TAddress extends string = string>(
  encodedAccount: EncodedAccount<TAddress> | MaybeEncodedAccount<TAddress>
):
  | Account<ProgramAuthority, TAddress>
  | MaybeAccount<ProgramAuthority, TAddress> {
  return decodeAccount(
    encodedAccount as MaybeEncodedAccount<TAddress>,
    getProgramAuthorityDecoder()
  );
}

export async function fetchProgramAuthority<TAddress extends string = string>(
  rpc: Parameters<typeof fetchEncodedAccount>[0],
  address: Address<TAddress>,
  config?: FetchAccountConfig
): Promise<Account<ProgramAuthority, TAddress>> {
  const maybeAccount = await fetchMaybeProgramAuthority(rpc, address, config);
  assertAccountExists(maybeAccount);
  return maybeAccount;
}

export async function fetchMaybeProgramAuthority<
  TAddress extends string = string,
>(
  rpc: Parameters<typeof fetchEncodedAccount>[0],
  address: Address<TAddress>,
  config?: FetchAccountConfig
): Promise<MaybeAccount<ProgramAuthority, TAddress>> {
  const maybeAccount = await fetchEncodedAccount(rpc, address, config);
  return decodeProgramAuthority(maybeAccount);
}

export async function fetchAllProgramAuthority(
  rpc: Parameters<typeof fetchEncodedAccounts>[0],
  addresses: Array<Address>,
  config?: FetchAccountsConfig
): Promise<Account<ProgramAuthority>[]> {
  const maybeAccounts = await fetchAllMaybeProgramAuthority(
    rpc,
    addresses,
    config
  );
  assertAccountsExist(maybeAccounts);
  return maybeAccounts;
}

export async function fetchAllMaybeProgramAuthority(
  rpc: Parameters<typeof fetchEncodedAccounts>[0],
  addresses: Array<Address>,
  config?: FetchAccountsConfig
): Promise<MaybeAccount<ProgramAuthority>[]> {
  const maybeAccounts = await fetchEncodedAccounts(rpc, addresses, config);
  return maybeAccounts.map((maybeAccount) =>
    decodeProgramAuthority(maybeAccount)
  );
}
